import type { Timestamp } from "@bufbuild/protobuf/wkt";
import { timestampDate } from "@bufbuild/protobuf/wkt";
import { format, formatDistanceToNowStrict, isValid } from "date-fns";

import { MessageState, BatchStatus, FailureReason } from "@/gen/bmi/v1/batch_message_pb";
import {
  AccountStatus,
  type Token,
  type WebhookDelivery,
} from "@/gen/wabms/admin/v1/admin_pb";

// ---------------------------------------------------------------------------
// Timestamps
// ---------------------------------------------------------------------------

/** Converts a protobuf Timestamp to a Date, tolerating absent optional fields. */
export function toDate(ts: Timestamp | undefined): Date | undefined {
  if (!ts) return undefined;
  const d = timestampDate(ts);
  return isValid(d) ? d : undefined;
}

/** Absolute, unambiguous, sortable. Used in tables. */
export function formatAbsolute(ts: Timestamp | undefined, fallback = "—"): string {
  const d = toDate(ts);
  return d ? format(d, "yyyy-MM-dd HH:mm:ss") : fallback;
}

/** Date only, for things like an expiry that has day granularity. */
export function formatDay(ts: Timestamp | undefined, fallback = "—"): string {
  const d = toDate(ts);
  return d ? format(d, "yyyy-MM-dd") : fallback;
}

/** "3 minutes ago" / "in 2 days". */
export function formatRelative(ts: Timestamp | undefined, fallback = "—"): string {
  const d = toDate(ts);
  if (!d) return fallback;
  return formatDistanceToNowStrict(d, { addSuffix: true });
}

/** Absolute value with the relative one in parentheses. */
export function formatBoth(ts: Timestamp | undefined, fallback = "—"): string {
  const d = toDate(ts);
  if (!d) return fallback;
  return `${format(d, "yyyy-MM-dd HH:mm")} (${formatDistanceToNowStrict(d, { addSuffix: true })})`;
}

// ---------------------------------------------------------------------------
// Tones
// ---------------------------------------------------------------------------

/**
 * The visual vocabulary of the whole app. Every status pill, dot and bar picks
 * one of these, so a colour always means the same thing on every screen.
 */
export type Tone = "neutral" | "info" | "progress" | "success" | "warning" | "danger";

const TONE_BADGE: Record<Tone, string> = {
  neutral: "border-border bg-muted text-muted-foreground",
  info: "border-transparent bg-tone-info/12 text-tone-info",
  progress: "border-transparent bg-tone-progress/12 text-tone-progress",
  success: "border-transparent bg-tone-success/12 text-tone-success",
  warning: "border-transparent bg-tone-warning/15 text-tone-warning",
  danger: "border-transparent bg-tone-danger/12 text-tone-danger",
};

const TONE_SOLID: Record<Tone, string> = {
  neutral: "bg-muted-foreground/40",
  info: "bg-tone-info",
  progress: "bg-tone-progress",
  success: "bg-tone-success",
  warning: "bg-tone-warning",
  danger: "bg-tone-danger",
};

const TONE_TEXT: Record<Tone, string> = {
  neutral: "text-muted-foreground",
  info: "text-tone-info",
  progress: "text-tone-progress",
  success: "text-tone-success",
  warning: "text-tone-warning",
  danger: "text-tone-danger",
};

export function toneBadgeClass(tone: Tone): string {
  return TONE_BADGE[tone];
}

export function toneSolidClass(tone: Tone): string {
  return TONE_SOLID[tone];
}

export function toneTextClass(tone: Tone): string {
  return TONE_TEXT[tone];
}

// ---------------------------------------------------------------------------
// Message state
// ---------------------------------------------------------------------------

const MESSAGE_STATE_LABEL: Record<MessageState, string> = {
  [MessageState.UNSPECIFIED]: "Unknown",
  [MessageState.REJECTED]: "Rejected",
  [MessageState.ACCEPTED]: "Accepted",
  [MessageState.SCHEDULED]: "Scheduled",
  [MessageState.QUEUED]: "Queued",
  [MessageState.SENDING]: "Sending",
  [MessageState.SENT]: "Sent",
  [MessageState.DELIVERED]: "Delivered",
  [MessageState.READ]: "Read",
  [MessageState.UNDELIVERED]: "Undelivered",
  [MessageState.FAILED]: "Failed",
  [MessageState.EXPIRED]: "Expired",
  [MessageState.CANCELED]: "Canceled",
};

const MESSAGE_STATE_TONE: Record<MessageState, Tone> = {
  [MessageState.UNSPECIFIED]: "neutral",
  [MessageState.REJECTED]: "danger",
  [MessageState.ACCEPTED]: "info",
  [MessageState.SCHEDULED]: "info",
  [MessageState.QUEUED]: "info",
  [MessageState.SENDING]: "progress",
  [MessageState.SENT]: "progress",
  [MessageState.DELIVERED]: "success",
  [MessageState.READ]: "success",
  [MessageState.UNDELIVERED]: "warning",
  [MessageState.FAILED]: "danger",
  [MessageState.EXPIRED]: "warning",
  [MessageState.CANCELED]: "neutral",
};

/** SPEC.md §4.1 ranks. Terminal states all sit at rank 6, except READ at 7. */
const MESSAGE_STATE_TERMINAL: ReadonlySet<MessageState> = new Set([
  MessageState.REJECTED,
  MessageState.DELIVERED,
  MessageState.READ,
  MessageState.UNDELIVERED,
  MessageState.FAILED,
  MessageState.EXPIRED,
  MessageState.CANCELED,
]);

/**
 * Order used for the per-state count bars, roughly lifecycle order so a batch
 * bar reads left to right as "progress".
 */
export const MESSAGE_STATE_ORDER: readonly MessageState[] = [
  MessageState.ACCEPTED,
  MessageState.SCHEDULED,
  MessageState.QUEUED,
  MessageState.SENDING,
  MessageState.SENT,
  MessageState.DELIVERED,
  MessageState.READ,
  MessageState.UNDELIVERED,
  MessageState.FAILED,
  MessageState.EXPIRED,
  MessageState.CANCELED,
  MessageState.REJECTED,
];

export function messageStateLabel(state: MessageState): string {
  return MESSAGE_STATE_LABEL[state] ?? "Unknown";
}

export function messageStateTone(state: MessageState): Tone {
  return MESSAGE_STATE_TONE[state] ?? "neutral";
}

/** CLIENTs must treat unrecognised values as non-terminal (SPEC.md §4.1). */
export function messageStateIsTerminal(state: MessageState): boolean {
  return MESSAGE_STATE_TERMINAL.has(state);
}

/** `MESSAGE_STATE_DELIVERED` -> MessageState.DELIVERED. */
export function messageStateFromProtoName(name: string): MessageState | undefined {
  const suffix = name.startsWith("MESSAGE_STATE_") ? name.slice("MESSAGE_STATE_".length) : name;
  const key = suffix.toUpperCase() as keyof typeof MessageState;
  const value = MessageState[key];
  return typeof value === "number" ? value : undefined;
}

/** MessageState.DELIVERED -> `MESSAGE_STATE_DELIVERED`. Map keys use this form. */
export function messageStateProtoName(state: MessageState): string {
  const key = MessageState[state];
  return typeof key === "string" ? `MESSAGE_STATE_${key}` : "MESSAGE_STATE_UNSPECIFIED";
}

// ---------------------------------------------------------------------------
// Batch status
// ---------------------------------------------------------------------------

export function batchStatusLabel(status: BatchStatus): string {
  switch (status) {
    case BatchStatus.PENDING:
      return "Pending";
    case BatchStatus.COMPLETED:
      return "Completed";
    case BatchStatus.UNSPECIFIED:
    default:
      return "Unknown";
  }
}

export function batchStatusTone(status: BatchStatus): Tone {
  switch (status) {
    case BatchStatus.PENDING:
      return "progress";
    case BatchStatus.COMPLETED:
      return "success";
    case BatchStatus.UNSPECIFIED:
    default:
      return "neutral";
  }
}

// ---------------------------------------------------------------------------
// Account status
// ---------------------------------------------------------------------------

const ACCOUNT_STATUS_LABEL: Record<AccountStatus, string> = {
  [AccountStatus.UNSPECIFIED]: "Unknown",
  [AccountStatus.UNPAIRED]: "Unpaired",
  [AccountStatus.CONNECTING]: "Connecting",
  [AccountStatus.CONNECTED]: "Connected",
  [AccountStatus.DISCONNECTED]: "Disconnected",
  [AccountStatus.LOGGED_OUT]: "Logged out",
  [AccountStatus.BANNED]: "Banned",
};

const ACCOUNT_STATUS_TONE: Record<AccountStatus, Tone> = {
  [AccountStatus.UNSPECIFIED]: "neutral",
  [AccountStatus.UNPAIRED]: "neutral",
  [AccountStatus.CONNECTING]: "progress",
  [AccountStatus.CONNECTED]: "success",
  [AccountStatus.DISCONNECTED]: "warning",
  [AccountStatus.LOGGED_OUT]: "danger",
  [AccountStatus.BANNED]: "danger",
};

/** One line of operator guidance per status. Shown next to the status dot. */
const ACCOUNT_STATUS_HINT: Record<AccountStatus, string> = {
  [AccountStatus.UNSPECIFIED]: "The service has not reported a status for this account yet.",
  [AccountStatus.UNPAIRED]: "Never paired. Scan a QR code or use a phone code to link a device.",
  [AccountStatus.CONNECTING]: "Handshaking with WhatsApp. Nothing to do.",
  [AccountStatus.CONNECTED]: "Online and sending within the send policy.",
  [AccountStatus.DISCONNECTED]:
    "Transiently offline. whatsmeow is reconnecting and messages keep queueing.",
  [AccountStatus.LOGGED_OUT]:
    "The companion device was unlinked from the phone. In-flight messages will fail until you re-pair.",
  [AccountStatus.BANNED]:
    "WhatsApp rejected the connection. Sending is hard-paused. Do not re-pair this number until you know why.",
};

export function accountStatusLabel(status: AccountStatus): string {
  return ACCOUNT_STATUS_LABEL[status] ?? "Unknown";
}

export function accountStatusTone(status: AccountStatus): Tone {
  return ACCOUNT_STATUS_TONE[status] ?? "neutral";
}

export function accountStatusHint(status: AccountStatus): string {
  return ACCOUNT_STATUS_HINT[status] ?? "";
}

/** These two need the operator to act; the Accounts page shouts about them. */
export function accountStatusNeedsAttention(status: AccountStatus): boolean {
  return status === AccountStatus.LOGGED_OUT || status === AccountStatus.BANNED;
}

// ---------------------------------------------------------------------------
// Failure reasons
// ---------------------------------------------------------------------------

const FAILURE_REASON_LABEL: Record<FailureReason, string> = {
  [FailureReason.UNSPECIFIED]: "Unspecified",
  [FailureReason.INVALID_DESTINATION]: "Invalid destination",
  [FailureReason.UNKNOWN_DESTINATION]: "Unknown destination",
  [FailureReason.BLOCKED_BY_RECIPIENT]: "Blocked by recipient",
  [FailureReason.WINDOW_EXPIRED]: "Messaging window expired",
  [FailureReason.CONTENT_REJECTED]: "Content rejected",
  [FailureReason.RATE_LIMITED]: "Rate limited",
  [FailureReason.TARGET_UNAVAILABLE]: "WhatsApp unavailable",
  [FailureReason.TARGET_AUTH_FAILED]: "WhatsApp auth failed",
  [FailureReason.EXPIRED]: "Expired",
  [FailureReason.CANCELED]: "Canceled",
  [FailureReason.INTERNAL]: "Internal error",
};

export function failureReasonLabel(reason: FailureReason): string {
  return FAILURE_REASON_LABEL[reason] ?? "Unknown";
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

const numberFormat = new Intl.NumberFormat(undefined);

export function formatCount(n: number): string {
  return numberFormat.format(n);
}

export function formatPercent(n: number, total: number): string {
  if (total <= 0) return "0%";
  return `${Math.round((n / total) * 100)}%`;
}

/** Truncates an opaque id for a table cell while keeping it recognisable. */
export function shortId(id: string, head = 8): string {
  return id.length <= head + 3 ? id : `${id.slice(0, head)}…`;
}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

/** The scopes CreateToken accepts, in the canonical order the server uses. */
export const SCOPES = ["service:read", "batches:read", "batches:write"] as const;
export type Scope = (typeof SCOPES)[number];

export type TokenStatus = "active" | "revoked" | "expired";

/**
 * Derived client-side because the contract carries timestamps, not a status.
 * `revoked` wins over `expired`: it is the deliberate act and the one an
 * operator is looking for.
 */
export function tokenStatus(token: Token, now: Date = new Date()): TokenStatus {
  if (token.revokedAt) return "revoked";
  const expires = toDate(token.expiresAt);
  if (expires && expires.getTime() < now.getTime()) return "expired";
  return "active";
}

// ---------------------------------------------------------------------------
// Webhook deliveries
// ---------------------------------------------------------------------------

export type DeliveryStatus = "delivered" | "pending" | "abandoned";

export function deliveryStatus(delivery: WebhookDelivery): DeliveryStatus {
  if (delivery.deliveredAt) return "delivered";
  if (delivery.abandonedAt) return "abandoned";
  return "pending";
}
